import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, PageHeader, Badge, Button, Modal, Input } from "@/components/ui";
import { toast } from "sonner";
import { Upload, Send, FileCheck2, Server, CheckCircle2, XCircle, Banknote, Settings2 } from "lucide-react";

export default function EdiTransport() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("834");
  const [content, setContent] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [transmissions, setTransmissions] = useState([]);
  // 835 remittance
  const [eraContent, setEraContent] = useState("");
  const [eraResult, setEraResult] = useState(null);
  const [remits, setRemits] = useState([]);
  // config editing
  const [editCfg, setEditCfg] = useState(null);
  const [cfgForm, setCfgForm] = useState({ host: "", port: "", username: "", password: "", remote_dir: "" });

  const loadTransport = () => {
    api.get("/transport/configs").then((r) => setConfigs(r.data));
    api.get("/transport/transmissions").then((r) => setTransmissions(r.data));
  };
  const loadRemits = () => api.get("/remittances").then((r) => setRemits(r.data)).catch(() => {});
  useEffect(() => { loadTransport(); loadRemits(); }, []);

  const genSample = async () => {
    const { data } = await api.post("/eligibility/generate-sample");
    setContent(data.content); setResult(null); toast.success("Sample 834 generated");
  };
  const ingest = async () => {
    if (!content.trim()) return toast.error("Paste or generate an 834 file first");
    setBusy(true);
    try {
      const { data } = await api.post("/eligibility/ingest", { content });
      setResult(data); toast.success(`Processed ${data.processed} records`);
    } catch { toast.error("Ingestion failed"); } finally { setBusy(false); }
  };
  const transmit = async (kind, config_id) => {
    setBusy(true);
    try {
      const { data } = await api.post("/transport/transmit", { kind, config_id });
      toast[data.status === "Delivered" ? "success" : "error"](`${kind} batch: ${data.status}`);
      loadTransport();
    } catch { toast.error("Transmission failed"); } finally { setBusy(false); }
  };

  const genEra = async () => {
    const { data } = await api.post("/remittance/generate-sample");
    setEraContent(data.content); setEraResult(null); toast.success("Sample 835 remittance generated");
  };
  const postEra = async () => {
    if (!eraContent.trim()) return toast.error("Paste or generate an 835 file first");
    setBusy(true);
    try {
      const { data } = await api.post("/remittance/post", { content: eraContent });
      setEraResult(data); loadRemits();
      toast.success(`Posted ${data.posted} payments · ${data.denied} denied`);
    } catch { toast.error("Remittance posting failed"); } finally { setBusy(false); }
  };

  const openEdit = (c) => {
    setEditCfg(c);
    setCfgForm({ host: c.host || "", port: c.port || "", username: c.username || "", password: "", remote_dir: c.remote_dir || "" });
  };
  const saveCfg = async () => {
    const body = {};
    ["host", "username", "remote_dir"].forEach((k) => { if (cfgForm[k] !== "") body[k] = cfgForm[k]; });
    if (cfgForm.port !== "") body.port = parseInt(cfgForm.port);
    if (cfgForm.password !== "") body.password = cfgForm.password;
    try {
      await api.patch(`/transport/configs/${editCfg.id}`, body);
      toast.success("Clearinghouse credentials updated");
      setEditCfg(null); loadTransport();
    } catch { toast.error("Update failed"); }
  };

  const TABS = [["834", "834 Eligibility Ingestion"], ["835", "835 Remittance Posting"], ["transport", "Clearinghouse Transport"]];

  return (
    <div className="vc-fade-up">
      <PageHeader title="EDI & Clearinghouse Transport" subtitle="834 eligibility ingestion, 835 remittance posting, and outbound 834/837 transmission with pre-submission validation and delivery logging." />

      <div className="flex gap-1 border-b border-border mb-5">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`edi-tab-${k}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-brand-700 text-brand-700" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{l}</button>
        ))}
      </div>

      {tab === "834" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-semibold">X12 834 File</h3>
              <Button size="sm" variant="outline" onClick={genSample} data-testid="gen-834-btn"><FileCheck2 className="h-4 w-4" /> Generate Sample</Button>
            </div>
            <textarea value={content} onChange={(e) => setContent(e.target.value)} data-testid="edi-834-input"
              placeholder="Paste an X12 834 Benefit Enrollment file, or generate a sample…"
              className="w-full h-72 rounded-md border border-input bg-brand-950 text-brand-100 font-mono text-xs p-3 focus:outline-none focus:ring-2 focus:ring-ring" />
            <Button className="w-full mt-3" onClick={ingest} disabled={busy} data-testid="ingest-834-btn"><Upload className="h-4 w-4" /> {busy ? "Ingesting…" : "Ingest Eligibility File"}</Button>
          </Card>
          <Card className="p-5">
            <h3 className="font-display font-semibold mb-4">Ingestion Result</h3>
            {result ? (
              <div className="space-y-3" data-testid="ingest-result">
                <div className="grid grid-cols-2 gap-3">
                  {[["Processed", result.processed], ["Added", result.added], ["Updated", result.updated], ["Terminated", result.terminated]].map(([l, v]) => (
                    <div key={l} className="rounded-md border border-border p-3"><p className="text-xs text-muted-foreground">{l}</p><p className="text-2xl font-bold font-display text-brand-700">{v}</p></div>
                  ))}
                </div>
                {result.errors?.length > 0 && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{result.errors.join(", ")}</div>}
                <p className="text-xs text-muted-foreground">834 records were parsed and reconciled against the member roster for the active tenant.</p>
              </div>
            ) : <p className="text-sm text-muted-foreground">Generate or paste an 834 file and ingest to see reconciliation results.</p>}
          </Card>
        </div>
      )}

      {tab === "835" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-semibold">X12 835 Remittance Advice</h3>
                <Button size="sm" variant="outline" onClick={genEra} data-testid="gen-835-btn"><FileCheck2 className="h-4 w-4" /> Generate Sample</Button>
              </div>
              <textarea value={eraContent} onChange={(e) => setEraContent(e.target.value)} data-testid="edi-835-input"
                placeholder="Paste an X12 835 Electronic Remittance Advice, or generate a sample from open claims…"
                className="w-full h-72 rounded-md border border-input bg-brand-950 text-brand-100 font-mono text-xs p-3 focus:outline-none focus:ring-2 focus:ring-ring" />
              <Button className="w-full mt-3" onClick={postEra} disabled={busy} data-testid="post-835-btn"><Banknote className="h-4 w-4" /> {busy ? "Posting…" : "Post Payments to Claims"}</Button>
            </Card>
            <Card className="p-5">
              <h3 className="font-display font-semibold mb-4">Posting Result</h3>
              {eraResult ? (
                <div className="space-y-3" data-testid="era-result">
                  <div className="grid grid-cols-2 gap-3">
                    {[["Processed", eraResult.processed], ["Payments Posted", eraResult.posted], ["Denied", eraResult.denied], ["Not Found", eraResult.not_found]].map(([l, v]) => (
                      <div key={l} className="rounded-md border border-border p-3"><p className="text-xs text-muted-foreground">{l}</p><p className="text-2xl font-bold font-display text-brand-700">{v}</p></div>
                    ))}
                  </div>
                  <div className="rounded-md bg-indigo-50 p-3 text-sm text-indigo-800 font-semibold">Total posted: ${eraResult.total_paid?.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground">Claims were matched by claim number within the active tenant and updated with paid amounts and status.</p>
                </div>
              ) : <p className="text-sm text-muted-foreground">Generate or paste an 835 file and post to update claim payments.</p>}
            </Card>
          </div>
          <Card className="overflow-hidden">
            <div className="px-5 py-4 border-b border-border"><h3 className="font-display font-semibold">Remittance Posting History</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Date</th><th className="px-4 py-3 font-semibold">Processed</th>
                  <th className="px-4 py-3 font-semibold">Posted</th><th className="px-4 py-3 font-semibold">Denied</th>
                  <th className="px-4 py-3 font-semibold">Total Paid</th><th className="px-4 py-3 font-semibold">By</th></tr></thead>
                <tbody className="divide-y divide-border">
                  {remits.length === 0 ? <tr><td colSpan="6" className="px-4 py-8 text-center text-muted-foreground">No remittances posted yet.</td></tr> :
                    remits.map((r) => (
                      <tr key={r.id} className="hover:bg-muted transition-colors" data-testid="remit-row">
                        <td className="px-4 py-2.5 text-xs">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                        <td className="px-4 py-2.5 font-mono">{r.processed}</td>
                        <td className="px-4 py-2.5 font-mono text-indigo-700">{r.posted}</td>
                        <td className="px-4 py-2.5 font-mono text-red-600">{r.denied}</td>
                        <td className="px-4 py-2.5 font-mono font-semibold">${(r.total_paid || 0).toLocaleString()}</td>
                        <td className="px-4 py-2.5 text-muted-foreground">{r.posted_by}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {tab === "transport" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {configs.map((c) => (
              <Card key={c.id} className="p-5" data-testid="transport-config">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><Server className="h-5 w-5 text-brand-700" /><h3 className="font-semibold">{c.name}</h3></div>
                  <div className="flex items-center gap-2">
                    <Badge tone="Low">{c.protocol}</Badge>
                    {isAdmin && <button onClick={() => openEdit(c)} className="text-muted-foreground hover:text-brand-700 transition-colors" data-testid={`edit-config-${c.id}`} title="Edit credentials"><Settings2 className="h-4 w-4" /></button>}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2 font-mono">{c.protocol === "SFTP" ? `sftp://${c.host}:${c.port}${c.remote_dir}` : `https://${c.host}${c.remote_dir}`}</p>
                <p className="text-[11px] text-muted-foreground mt-1">User: <span className="font-mono">{c.username || "—"}</span> · Password: <span className="font-mono">{c.password ? "••••••••" : "not set"}</span></p>
                <div className="flex flex-wrap gap-1 mt-3">{c.formats.map((f) => <span key={f} className="text-[10px] rounded bg-slate-100 px-1.5 py-0.5">{f}</span>)}</div>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" onClick={() => transmit("834", c.id)} disabled={busy} data-testid={`transmit-834-${c.id}`}><Send className="h-3.5 w-3.5" /> Send 834</Button>
                  <Button size="sm" onClick={() => transmit("837", c.id)} disabled={busy} data-testid={`transmit-837-${c.id}`}><Send className="h-3.5 w-3.5" /> Send 837 Batch</Button>
                </div>
              </Card>
            ))}
          </div>

          <Card className="overflow-hidden">
            <div className="px-5 py-4 border-b border-border"><h3 className="font-display font-semibold">Transmission Log</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">File</th><th className="px-4 py-3 font-semibold">Kind</th>
                  <th className="px-4 py-3 font-semibold">Endpoint</th><th className="px-4 py-3 font-semibold">Records</th>
                  <th className="px-4 py-3 font-semibold">Validation</th><th className="px-4 py-3 font-semibold">Status</th></tr></thead>
                <tbody className="divide-y divide-border">
                  {transmissions.length === 0 ? <tr><td colSpan="6" className="px-4 py-8 text-center text-muted-foreground">No transmissions yet. Send a batch above.</td></tr> :
                    transmissions.map((t) => (
                      <tr key={t.id} className="hover:bg-muted transition-colors">
                        <td className="px-4 py-2.5 font-mono text-xs">{t.filename}</td>
                        <td className="px-4 py-2.5"><span className="text-xs rounded bg-slate-100 px-2 py-0.5">{t.kind}</span></td>
                        <td className="px-4 py-2.5 text-muted-foreground">{t.endpoint}</td>
                        <td className="px-4 py-2.5 font-mono">{t.record_count}</td>
                        <td className="px-4 py-2.5">{t.validation?.valid ? <span className="flex items-center gap-1 text-indigo-700 text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> Passed</span> : <span className="flex items-center gap-1 text-red-600 text-xs"><XCircle className="h-3.5 w-3.5" /> Failed</span>}</td>
                        <td className="px-4 py-2.5"><Badge tone={t.status === "Delivered" ? "Completed" : t.status === "Failed" || t.status === "Rejected" ? "Denied" : "Pending"}>{t.status}</Badge></td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      <Modal open={!!editCfg} onClose={() => setEditCfg(null)} title={`Edit Credentials — ${editCfg?.name || ""}`}>
        <div className="space-y-3">
          <div><label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Host</label><Input className="mt-1.5" value={cfgForm.host} onChange={(e) => setCfgForm({ ...cfgForm, host: e.target.value })} data-testid="cfg-host" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Port</label><Input className="mt-1.5" type="number" value={cfgForm.port} onChange={(e) => setCfgForm({ ...cfgForm, port: e.target.value })} data-testid="cfg-port" /></div>
            <div><label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Remote Dir</label><Input className="mt-1.5" value={cfgForm.remote_dir} onChange={(e) => setCfgForm({ ...cfgForm, remote_dir: e.target.value })} data-testid="cfg-dir" /></div>
          </div>
          <div><label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Username</label><Input className="mt-1.5" value={cfgForm.username} onChange={(e) => setCfgForm({ ...cfgForm, username: e.target.value })} data-testid="cfg-username" /></div>
          <div><label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Password</label><Input className="mt-1.5" type="password" placeholder="Leave blank to keep current" value={cfgForm.password} onChange={(e) => setCfgForm({ ...cfgForm, password: e.target.value })} data-testid="cfg-password" /><p className="text-[11px] text-muted-foreground mt-1">Live SFTP delivery activates automatically once a host and password are set.</p></div>
          <Button className="w-full" onClick={saveCfg} data-testid="cfg-save">Save Credentials</Button>
        </div>
      </Modal>
    </div>
  );
}
